import { hashKey, QueryClient, type Query, type QueryKey } from "@tanstack/react-query";

// 읽기 쿼리 키의 머리 — 무효화(D5)가 이 머리로 활성 읽기만 고른다.
export const VIEW_KEY = "view";

// 서명 URL 캐시 키의 머리 — 계정이 바뀌면 같이 버린다(서명에 조직이 박혀 있다).
export const SIGNED_URL_KEY = "signed-url";

// 네트워크 오류(fetch 의 TypeError)만 한 번 더 묻는다 — 5xx·설정 오류까지 다시 물으면 숨은 탭에서 재시도가 멈춰 빈 화면으로 남는다(legacy SSR 은 다시 묻지 않는다).
const retry = (failureCount: number, error: unknown): boolean =>
  error instanceof TypeError && failureCount < 1;

export const createQueryClient = (): QueryClient =>
  new QueryClient({ defaultOptions: { queries: { retry, refetchOnWindowFocus: false } } });

export const queryClient = createQueryClient();

// 액션 봉투에 invalidate 가 오면 — 지금 화면에 붙은 읽기 전부를 다시 읽는다(D5) · router.refresh() 는 아래 refreshActiveViews.
export const invalidateActiveViews = (client: QueryClient = queryClient): Promise<void> =>
  client.invalidateQueries({ queryKey: [VIEW_KEY], type: "active" });

// 화면에서 떼어진 읽기를 버린다 — 봉투 invalidate 는 활성 읽기만 다시 읽어, 돌아온 화면의 loader(ensureQueryData)가 옛 캐시로 그리지 않게.
export const dropDetachedViews = (keys: readonly QueryKey[], client: QueryClient = queryClient): void => {
  for (const queryKey of keys) client.removeQueries({ queryKey, predicate: (query) => query.getObserversCount() === 0 });
};

// 액션(rpc POST) 응답이 온 횟수 · 읽기마다 시작할 때의 그 값 — refresh 가 「마지막 액션 응답 뒤에 이미 다시 읽고 있는」 읽기를 알아본다.
let actionsSettled = 0;
const fetchStartedAt = new Map<string, number>();

export const noteActionSettled = (): void => {
  actionsSettled += 1;
};

export const noteViewFetch = (queryKey: QueryKey): void => {
  fetchStartedAt.set(hashKey(queryKey), actionsSettled);
};

const fetchingSinceLastAction = (query: Query): boolean =>
  query.state.fetchStatus === "fetching" && fetchStartedAt.get(query.queryHash) === actionsSettled;

// router.refresh() — 봉투 invalidate 가 막 다시 읽기 시작한 읽기는 건너뛴다(같은 GET 두 번 · 분당 한도) · 나머지 활성 읽기는 다시 읽는다.
export const refreshActiveViews = (client: QueryClient = queryClient): Promise<void> =>
  client.invalidateQueries({ queryKey: [VIEW_KEY], type: "active", predicate: (query) => !fetchingSinceLastAction(query) });
